// Wizard Nieuwe reservering: van wat de balie koos naar het verzoek voor de api, voor de berekening en het aanmaken.
import { useCustom } from "@refinedev/core";

import { useLocatieUrl } from "./locatie";

import type { Extras } from "./ArrangementenStap";
import { RESERVERING, dierSleutel } from "./ArrangementenStap";
import { geldigePeriode, type GekozenKlant, type Periode } from "./bouwstenen";
import { antwoordenVoorApi, blokAan, stapVan, type Antwoorden, type Indeling, type StapSoort } from "./indeling";
import type { DierVerblijf } from "./VerblijfStap";

/** De laatste keuzes: verzekering, wensen van de klant en een interne notitie. */
export interface Keuzes {
  annuleringsverzekering: boolean;
  /** De gekozen andere reserveringsproducten (medifonds, borg, …), op id. */
  producten?: number[];
  overigeWensen: string;
  interneNotitie: string;
}

/** De indeling waarmee de wizard werkt, en de antwoorden op de eigen vragen. */
export interface WizardGegevens {
  versie: number;
  indeling: Indeling;
  antwoorden: Antwoorden;
}

/** Uit POST /api/pension/reserveringen/nieuw/berekening. */
export interface Berekening {
  nachten: number;
  dagen: number;
  dieren: {
    dierId: number;
    naam: string | null;
    accommodatie: string | null;
    dagprijsLaag: number;
    dagprijsHoog: number;
    dagenLaag: number;
    dagenHoog: number;
    toeslag: number;
    bedrag: number;
  }[];
  verblijf: number;
  extras: {
    dierId: number | null;
    arrangementId: number;
    naam: string;
    aantal: number;
    eenheden: number;
    prijs: number;
    bedrag: number;
  }[];
  extrasTotaal: number;
  verzekeringAangeboden: boolean;
  verzekeringPrijs: number | null;
  verzekering: number;
  totaal: number;
  aanbetalingPercentage: number;
  aanbetaling: number;
  /** De andere reserveringsproducten die de vestiging aanbiedt, met wat ze bij deze reservering kosten. */
  producten?: { id: number; naam: string; prijs: number; gekozen: boolean }[];
}

const leeg = (tekst: string) => (tekst.trim() === "" ? null : tekst.trim());

const lijst = (keuzes: Record<number, number> | undefined) =>
  Object.entries(keuzes ?? {}).map(([id, aantal]) => ({ arrangementId: Number(id), aantal }));

/**
 * Het verzoek zoals de api het wil: per dier zijn verblijf en extra's, en de keuzes voor de hele reservering. Wat de
 * indeling uit heeft staan (een stap, of een blok in een stap) gaat niet mee: dat heeft de balie niet gevraagd.
 */
export function maakVerzoek(
  klant: GekozenKlant,
  dierIds: number[],
  periode: Periode,
  verblijf: Record<number, DierVerblijf>,
  extras: Extras,
  keuzes: Keuzes,
  wizard?: WizardGegevens,
) {
  const aan = (soort: StapSoort, blok?: string) => {
    if (!wizard) return true;
    const stap = stapVan(wizard.indeling, soort);
    return !!stap?.actief && (blok == null || blokAan(stap, blok));
  };
  const typeAan = aan("VERBLIJF", "TYPE_VERBLIJF");
  const tariefAan = aan("VERBLIJF", "TARIEF");
  const voedingAan = aan("VERBLIJF", "VOEDING");
  const medicatieAan = aan("VERBLIJF", "MEDICATIE");
  const verzorgingAan = aan("VERBLIJF", "VERZORGING");
  const bijzonderhedenAan = aan("VERBLIJF", "BIJZONDERHEDEN");
  const arrangementenAan = aan("ARRANGEMENTEN");
  return {
    klantId: klant.id,
    aankomst: periode.aankomst,
    aankomstTijd: periode.aankomstTijd,
    vertrek: periode.vertrek,
    vertrekTijd: periode.vertrekTijd,
    dieren: dierIds.map((id) => {
      const v = verblijf[id];
      const voeding = voedingAan ? v?.voeding : undefined;
      const categorie = voeding?.startsWith("categorie:") ? Number(voeding.slice("categorie:".length)) : null;
      return {
        dierId: id,
        prijscategorieId: tariefAan ? (v?.prijscategorieId ?? null) : null,
        accommodatieTypeId: typeAan ? (v?.typeId ?? null) : null,
        voeding: categorie != null ? "CATEGORIE" : (voeding ?? "STANDAARD"),
        voedingCategorieId: categorie,
        voedingInstructies: voedingAan && v ? leeg(v.voedingInstructies) : null,
        medicatie: medicatieAan ? (v?.medicatie ?? false) : false,
        medicatieInstructies: medicatieAan && v?.medicatie ? leeg(v.medicatieInstructies) : null,
        borstelSoortId: verzorgingAan && v?.borstelen ? Number(v.borstelen) : null,
        wassenSoortId: verzorgingAan && v?.wassen ? Number(v.wassen) : null,
        samenMetGezin: verzorgingAan ? (v?.samenMetGezin ?? false) : false,
        bijzonderheden: bijzonderhedenAan && v ? leeg(v.bijzonderheden) : null,
        extras: arrangementenAan ? lijst(extras[dierSleutel(id)]) : [],
      };
    }),
    extras: arrangementenAan ? lijst(extras[RESERVERING]) : [],
    annuleringsverzekering: aan("KEUZES", "VERZEKERING") && keuzes.annuleringsverzekering,
    producten: aan("KEUZES", "VERZEKERING") ? (keuzes.producten ?? []) : [],
    overigeWensen: aan("KEUZES", "WENSEN") ? leeg(keuzes.overigeWensen) : null,
    interneNotitie: aan("KEUZES", "NOTITIE") ? leeg(keuzes.interneNotitie) : null,
    concept: false,
    wizardVersie: wizard?.versie ?? 0,
    antwoorden: wizard ? antwoordenVoorApi(wizard.indeling, wizard.antwoorden, dierIds) : [],
  };
}

/** De berekening van wat er nu gekozen is; Refine rekent opnieuw zodra er iets verandert. De antwoorden tellen niet mee. */
export function useBerekening(verzoek: ReturnType<typeof maakVerzoek>, periode: Periode) {
  const locatie = useLocatieUrl();
  return useCustom<Berekening>({
    url: locatie("pension/reserveringen/nieuw/berekening"),
    method: "post",
    config: { payload: { ...verzoek, antwoorden: [] } },
    queryOptions: { enabled: geldigePeriode(periode) && verzoek.dieren.length > 0 },
  });
}
